import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { participantService, authService } from '../../services';
import useWebSocket from '../../hooks/useWebSocket';
import useWakeLock from '../../hooks/useWakeLock';
import useSimulationAutoJoin from '../../hooks/useSimulationAutoJoin';
import useMicroSimAutoJoin from '../../hooks/useMicroSimAutoJoin';
import ReconnectBanner from '../../components/participant/ReconnectBanner';
import SessionPhaseWelcome from '../../components/participant/SessionPhaseWelcome';
import ConnectionStatus from '../../components/session/ConnectionStatus';
import { useNotifications } from '../../contexts/NotificationContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import tokenStorage from '../../utils/tokenStorage';
import logoFull from '../../assets/images/logo_aktivity.png';
import logoIcon from '../../assets/images/standalone_logo.png';

// Import activity components
import PollActivity from '../../components/activities/PollActivity';
import QuizActivity from '../../components/activities/QuizActivity';
import WordCloudActivity from '../../components/activities/WordCloudActivity';
import QAActivity from '../../components/activities/QAActivity';
import FeedbackActivity from '../../components/activities/FeedbackActivity';
import ChallengesActivity from '../../components/activities/ChallengesActivity';
import ActionPlansActivity from '../../components/activities/ActionPlansActivity';
import OpenQuestionActivity from '../../components/activities/OpenQuestionActivity';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Participant Active Session Page
 * Dashboard-style layout with sidebar navigation
 */

// Resolve the timeLimit (seconds) from any of the activity's legacy fields.
// Require explicit `timerEnabled === true` — the timer is disabled by default,
// so any activity that doesn't opt in (including legacy docs with lingering
// duration/timeLimit values) is treated as untimed.
const resolveTimeLimitSec = (a) => {
  if (!a) return 0;
  if (a.timerEnabled !== true) return 0;
  if (typeof a.timeLimit === 'number' && a.timeLimit > 0) return a.timeLimit;
  if (a.config?.timeLimit > 0) return a.config.timeLimit;
  if (a.duration > 0) return a.duration * 60;
  return 0;
};

// Compute the seconds remaining for an activity based on its server-recorded
// launchedAt + timeLimit. Returns 0 when no timer is configured (trainer
// disabled the timer) — the activity runs indefinitely until manually stopped.
const computeInitialRemaining = (a) => {
  const seconds = resolveTimeLimitSec(a);
  if (!seconds) return 0; // no timer — runs until trainer stops it
  if (!a?.launchedAt) return seconds;
  const launchTs = new Date(a.launchedAt).getTime();
  if (isNaN(launchTs)) return seconds;
  return Math.max(0, Math.round((launchTs + seconds * 1000 - Date.now()) / 1000));
};

// Whether an activity has a timer enabled (positive timeLimit / duration).
const hasTimer = (a) => resolveTimeLimitSec(a) > 0;

const ParticipantActiveSessionPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useNotifications();
  const { formatDateWithTimezone } = usePreferences();
  const [session, setSession] = useState(null);
  const [currentActivity, setCurrentActivity] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [hasResponded, setHasResponded] = useState(false);
  // True only when the participant already submitted a response BEFORE this
  // activity was launched (i.e. trainer relaunched it, or page just loaded
  // mid-relaunch). Distinguishes the "you've already responded — please wait"
  // view from the post-fresh-submit results view (which we still want to show
  // for participants who answered during the current run).
  const [respondedFromPriorRun, setRespondedFromPriorRun] = useState(false);
  // Initialize showActivitiesList from sessionStorage to preserve navigation state
  const [showActivitiesList, setShowActivitiesList] = useState(() => {
    try {
      const saved = sessionStorage.getItem(`participant_view_${sessionId}`);
      return saved ? saved === 'list' : true;
    } catch {
      return true;
    }
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Which view is shown in the main content area: 'activities' (default) or 'welcome'.
  // 'welcome' renders the SessionPhaseWelcome component inline, same block as the lobby's session-phase welcome.
  // Honor `?view=welcome` in the URL so Welcome links from Notes/Workbooks/Reports pages land here on the welcome view.
  const [activeView, setActiveView] = useState(() => {
    const params = new URLSearchParams(location.search);
    return params.get('view') === 'welcome' ? 'welcome' : 'activities';
  });

  // Track launched activities and responded activities
  const [launchedActivities, setLaunchedActivities] = useState([]);
  const [respondedActivities, setRespondedActivities] = useState(new Set());

  // Participant info for response matching
  const [participantId, setParticipantId] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [participantCode, setParticipantCode] = useState('');


  // Launched workbook
  const [launchedWorkbook, setLaunchedWorkbook] = useState(null);

  // How many workbooks this participant has opened (powers the "Content Viewed %"
  // row in the welcome card). Fetched from the same endpoint the lobby uses.
  const [workbookViewsCount, setWorkbookViewsCount] = useState(0);

  // Session ended modal
  const [showSessionEndedModal, setShowSessionEndedModal] = useState(false);
  const [sessionEndReason, setSessionEndReason] = useState('');
  // Session paused modal (for multi-day sessions)
  const [showSessionPausedModal, setShowSessionPausedModal] = useState(false);
  const [pausedCodeCopied, setPausedCodeCopied] = useState(false);
  // Break popup
  const [showBreakModal, setShowBreakModal] = useState(false);
  const [breakInfo, setBreakInfo] = useState({ name: '', breakTime: 0 });
  // Subsession started popup (when break ends)
  const [showSubsessionStartedModal, setShowSubsessionStartedModal] = useState(false);
  const [subsessionStartedInfo, setSubsessionStartedInfo] = useState({ name: '', number: 0 });
  // Use ref to prevent multiple session ended handlers without causing re-renders
  const sessionEndedHandledRef = useRef(false);
  // Track last pathname to detect navigation to this page
  const lastPathnameRef = useRef(null);
  // Track if initial fetch has been done
  const initialFetchDoneRef = useRef(false);
  // Track the last location key to detect fresh navigations
  const lastLocationKeyRef = useRef(null);
  // Overall session feedback - 5 questions format
  const [showOverallFeedbackModal, setShowOverallFeedbackModal] = useState(false);
  const [overallFeedbackSubmitted, setOverallFeedbackSubmitted] = useState(false);
  const [overallFeedbackRatings, setOverallFeedbackRatings] = useState({
    topicRelevance: 0,
    trainerEffectiveness: 0,
    confidenceToApply: 0,
    learningAdvocacy: 0,
    challengeResolution: 0,
  });
  const [overallFeedbackComment, setOverallFeedbackComment] = useState('');
  // Launch status of overall feedback (persists across refresh/rejoin, unlike the socket-only popup)
  const [overallFeedbackStatus, setOverallFeedbackStatus] = useState({
    launched: false,
    endedAt: null,
    hasSubmitted: false,
    config: {},
  });

  // Activity responses
  const [pollResponse, setPollResponse] = useState(null);
  const [pollResults, setPollResults] = useState(null);
  const [quizResults, setQuizResults] = useState(null);
  const [wordCloudWord, setWordCloudWord] = useState('');
  const [feedbackData, setFeedbackData] = useState(null);
  const [challengeData, setChallengeData] = useState(null);
  const [actionPlanData, setActionPlanData] = useState(null);
  const [openQuestionData, setOpenQuestionData] = useState(null);

  // Q&A questions - initially empty, populated from real-time WebSocket data
  const [qaQuestions] = useState([]);

  // Navigation items for sidebar
  // Check if session is completed to show additional menu items
  const isSessionCompleted = session?.status === 'completed' || session?.status === 'ended';

  // Keep the screen awake while the session is active so participants don't
  // miss activity launches when their phone auto-locks. Silent no-op on
  // browsers without the Wake Lock API (pre-iOS 16.4, etc.).
  useWakeLock(session?.status === 'live' || session?.status === 'paused');

  // Check if it's a multi-day session (different start and end dates)
  const isMultiDaySession = session?.startTime && session?.endTime &&
    new Date(session.startTime).toISOString().slice(0, 10) !== new Date(session.endTime).toISOString().slice(0, 10);

  // Show report/content items when session is completed OR when paused (for multi-day sessions)
  const showReportAndContent = isSessionCompleted || (session?.status === 'paused' && isMultiDaySession);

  // Base navigation items (always visible)
  const baseNavigationItems = [
    {
      id: 'welcome',
      name: 'Welcome',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      onClick: () => {
        // Show the session-phase welcome inline (same block as lobby) instead of routing away.
        setActiveView('welcome');
        setCurrentActivity(null);
        setShowActivitiesList(false);
        // Scroll the main area up so the welcome content is visible, not hidden below the activities scroll position.
        window.scrollTo({ top: 0, behavior: 'instant' });
      },
    },
    {
      id: 'activities',
      name: 'Aktivities',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
      ),
      onClick: () => {
        // Always show activities list when clicking Aktivities nav
        setActiveView('activities');
        setShowActivitiesList(true);
        setCurrentActivity(null);
        // Don't navigate if already on the page - just update state
        if (!window.location.pathname.includes('/active')) {
          navigate(`/session/${sessionId}/active`);
        }
      },
      badge: session?.status === 'live' ? 'Live' : null,
    },
    {
      id: 'workspace',
      name: 'My Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/notes`),
    },
  ];

  // Items only shown after session is completed
  const completedSessionItems = [
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/workbooks`),
    },
    {
      id: 'reports',
      name: 'Reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: () => navigate(`/session/${sessionId}/reports`),
    },
  ];

  // Combine navigation items based on session status
  // Show content and reports when session is completed OR paused (for multi-day sessions)
  const navigationItems = showReportAndContent
    ? [...baseNavigationItems, ...completedSessionItems]
    : baseNavigationItems;

  const bottomNavItems = [
    {
      id: 'help',
      name: 'Help Center',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      onClick: () => {},
    },
  ];

  // WebSocket connection
  const socket = useWebSocket(sessionId, {
    enableSimulation: false,
  });

  // Hand this phone over when the trainer starts something it should be on: a team
  // simulation (teams assigned, or reconnecting mid-run) or a micro simulation (paired).
  // Both reuse THIS page's socket — called after it, never opening their own — so the
  // connection banner reflects the real connection.
  useSimulationAutoJoin(socket, sessionId, participantId);
  useMicroSimAutoJoin(socket, sessionId);

  // Detect when the socket fails to connect within 10 seconds of page load.
  // Happens on Lockdown Mode, aggressive content blockers, or corporate proxies
  // that kill WebSockets. Without this, the participant sees a working page
  // that never updates and doesn't know why.
  const [socketBlocked, setSocketBlocked] = useState(false);
  useEffect(() => {
    if (socket?.isConnected) {
      setSocketBlocked(false);
      return undefined;
    }
    const timer = setTimeout(() => {
      if (!socket?.isConnected) setSocketBlocked(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, [socket?.isConnected]);

  // Demo activities
  const demoActivities = useMemo(() => [
    {
      id: 'act-poll-001',
      type: 'poll',
      icon: '📊',
      title: 'DISC Knowledge Check',
      question: 'Which DISC style is characterized by being analytical, precise, and detail-oriented?',
      config: {
        pollType: 'single',
        options: [
          'D - Dominance (Direct & Decisive)',
          'I - Influence (Enthusiastic & Persuasive)',
          'S - Steadiness (Patient & Team Player)',
          'C - Conscientiousness (Analytical & Precise)',
        ],
        anonymous: true,
        showResults: true,
        correctAnswer: 3,
      },
      timeLimit: 120,
    },
    {
      id: 'act-quiz-001',
      type: 'quiz',
      icon: '❓',
      title: 'Emotional Intelligence Quiz',
      question: 'Test your understanding of Emotional Intelligence domains',
      config: {
        questions: [
          {
            id: 1,
            question: 'Which competency is part of Relationship Management in EI?',
            options: ['Adaptability', 'Empathy', 'Conflict management', 'Positive outlook'],
            correctAnswer: 2,
          },
        ],
      },
      timeLimit: 180,
    },
    {
      id: 'act-wordcloud-001',
      type: 'word_cloud',
      icon: '☁️',
      title: 'Team Strengths',
      question: 'What strengths do you bring to your team?',
      config: {
        minLength: 2,
        maxLength: 20,
      },
      timeLimit: 90,
    },
    {
      id: 'act-qna-001',
      type: 'qna',
      icon: '🙋',
      title: 'Q&A Session',
      question: 'What difficult conversations have you been avoiding?',
      config: {
        allowAnonymous: true,
        maxQuestions: 3,
      },
      timeLimit: 600,
    },
    {
      id: 'act-challenges-001',
      type: 'challenges',
      icon: '⚠️',
      title: 'Share Your Challenges',
      question: 'What challenges are you currently facing in your role?',
      config: {
        allowAnonymous: true,
        maxChallenges: 3,
      },
      timeLimit: 300,
    },
    {
      id: 'act-actionplans-001',
      type: 'actionplans',
      icon: '📋',
      title: 'Create Your Action Plan',
      question: 'What actions will you take after this session?',
      config: {
        minActions: 1,
        maxActions: 5,
      },
      timeLimit: 300,
    },
    {
      id: 'act-feedback-001',
      type: 'feedback',
      icon: '💬',
      title: 'Session Feedback',
      question: 'How was your experience in this session?',
      config: {
        includeRating: true,
        includeComments: true,
      },
      timeLimit: 180,
    },
  ], []);

  useEffect(() => {
    // Get participant info with cookie recovery fallback for Safari/iOS
    const initParticipantData = async () => {
      try {
        let participantData = tokenStorage.getParticipantData(sessionId);

        // If no participant data, try cookie recovery (Safari/iOS fallback)
        if (!participantData || !participantData.participantId) {
          try {
            const response = await participantService.recoverSession();
            if (response.success && response.data) {
              const recovered = response.data;
              participantData = {
                participantId: recovered.participantId,
                sessionId: recovered.sessionId,
                sessionCode: recovered.sessionCode,
                name: recovered.participantName,
                email: recovered.email || '',
                organization: recovered.organization || '',
                participantCode: recovered.participantCode,
                isMultiDay: recovered.isMultiDay,
              };
              tokenStorage.setItem('current_participant', JSON.stringify(participantData));
              tokenStorage.setItem(`participant_${recovered.sessionId}`, JSON.stringify(participantData));
            }
          } catch {
            // Cookie recovery failed — try one more time after a short delay
            // Safari ITP can cause transient storage failures that resolve on retry
            try {
              await new Promise(resolve => setTimeout(resolve, 500));
              participantData = tokenStorage.getParticipantData(sessionId);
            } catch {
              // Final fallback failed
            }
          }
        }

        // If still no participant data, redirect to join page
        // But preserve the session ID in the redirect URL so they can rejoin the same session
        if (!participantData || !participantData.participantId) {
          console.warn('[Participant] No participant data found after recovery attempts, redirecting to join');
          navigate('/join', { replace: true });
          return;
        }

        // Ensure all storage keys are synced (covers recovery scenarios)
        tokenStorage.setParticipantData(sessionId, participantData);

        if (participantData.participantId) {
          setParticipantId(participantData.participantId);
        }
        if (participantData.name) {
          setParticipantName(participantData.name);
        }
        if (participantData.participantCode) {
          setParticipantCode(participantData.participantCode);
        }
      } catch (e) {
        console.error('[Participant] Error parsing participant data:', e);
        window.history.replaceState(null, '', '/join');
        navigate('/join', { replace: true });
        return;
      }

      // Fetch session data and active activities from API
      const fetchSessionData = async () => {
      try {
        // Fetch session info
        const sessionResponse = await participantService.getSession(sessionId);
        if (sessionResponse.success && sessionResponse.data) {
          setSession(sessionResponse.data);

          // Initialize respondedActivities from participant's existing responses
          const participantResponses = sessionResponse.data.participantResponses || sessionResponse.data.responses || [];
          if (participantResponses.length > 0) {
            const respondedIds = new Set(
              participantResponses.map(r => r.activityId || r.activity).filter(Boolean)
            );
            setRespondedActivities(prev => new Set([...prev, ...respondedIds]));
          }
        }

        // Non-drip mode: load ALL activities from all subsessions upfront so
        // participants see every activity at once (no need for trainer to
        // launch one by one). Drip mode: only show trainer-launched activities.
        const sessionData = sessionResponse?.data;
        const isNonDrip = sessionData?.sessionMode === 'non-drip';

        if (isNonDrip && sessionData?.subSessions?.length > 0) {
          console.log('[Participant] Non-drip mode — loading all activities from all subsessions');
          const allActivities = [];
          sessionData.subSessions.forEach((ss) => {
            (ss.activities || []).forEach((act) => {
              allActivities.push({
                ...act,
                _id: act._id || act.id,
                subSessionId: ss._id || ss.id,
                subSessionName: ss.name || ss.title,
                launchedAt: act.launchedAt || sessionData.actualStartTime || new Date(),
              });
            });
          });
          if (allActivities.length > 0) {
            setLaunchedActivities(allActivities);
            console.log(`[Participant] Non-drip: ${allActivities.length} activities loaded`);
          }
        }

        // Fetch active activities (for page refresh persistence + drip mode)
        console.log('[Participant] Fetching active activities for session:', sessionId);
        const activitiesResponse = await participantService.getActiveActivities(sessionId);
        console.log('[Participant] Activities API response:', activitiesResponse);
        if (activitiesResponse.success && activitiesResponse.data) {
          const activeActivities = activitiesResponse.data;
          console.log('[Participant] Active activities count:', activeActivities?.length || 0);
          if (activeActivities && activeActivities.length > 0) {
            console.log('[Participant] Loaded active activities:', activeActivities);
            // In non-drip mode, merge with existing (avoid duplicates).
            // In drip mode, replace entirely.
            if (isNonDrip) {
              setLaunchedActivities(prev => {
                const existingIds = new Set(prev.map(a => (a._id || a.id)?.toString()));
                const newOnes = activeActivities
                  .filter(a => !existingIds.has((a._id || a.id)?.toString()))
                  .map(act => ({ ...act, launchedAt: act.launchedAt || new Date() }));
                return [...prev, ...newOnes];
              });
            } else {
              setLaunchedActivities(activeActivities.map(act => ({
                ...act,
                launchedAt: act.launchedAt || new Date(),
              })));
            }

            // Check for activities that participant has already responded to
            const respondedIds = new Set();
            activeActivities.forEach(act => {
              const actId = act._id || act.id;
              // Check if activity has hasResponded flag or userResponse
              if (act.hasResponded || act.userResponse || act.responded) {
                respondedIds.add(actId);
              }
              // Also check localStorage for response data
              const localStorageKeys = [
                `poll_${sessionId}_${actId}`,
                `quiz_${sessionId}_${actId}`,
                `wordcloud_${sessionId}_${actId}`,
                `feedback_${sessionId}_${actId}`,
                `challenge_${sessionId}_${actId}`,
                `actionplan_${sessionId}_${actId}`,
                `openquestion_${sessionId}_${actId}`,
              ];
              const hasLocalResponse = localStorageKeys.some(key => localStorage.getItem(key));
              if (hasLocalResponse) {
                respondedIds.add(actId);
              }
            });
            if (respondedIds.size > 0) {
              setRespondedActivities(prev => new Set([...prev, ...respondedIds]));
            }

            // If there's a current active activity that user hasn't responded to, show it
            const currentActive = activeActivities.find(a => {
              const isActive = a.status === 'active' || a.isLive;
              const actId = a._id || a.id;
              const hasResponded = respondedIds.has(actId);
              return isActive && !hasResponded;
            });

            // ALWAYS show active activity if one exists and user hasn't responded
            // This ensures newly launched activities are shown even when navigating from other pages
            if (currentActive) {
              console.log('[Participant] Auto-showing active activity:', currentActive.title);
              setCurrentActivity(currentActive);
              setShowActivitiesList(false);
              // Clear the sessionStorage to reset view state for new activity
              sessionStorage.removeItem(`participant_view_${sessionId}`);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching session data:', error);
      }
    };

      fetchSessionData();
    };

    initParticipantData();
  }, [sessionId]);

  // Keep the "Content Viewed %" metric accurate by re-fetching the participant's
  // workbook-view count when the welcome view opens or the active view toggles.
  // Without this the welcome card would always show 0% on the active-session page.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const fetchWorkbookViews = async () => {
      try {
        const res = await participantService.getWorkbookViews(sessionId);
        if (!cancelled && res?.success && res?.data) {
          setWorkbookViewsCount(res.data.totalViewed || 0);
        }
      } catch (err) {
        // Non-fatal — just leave the count at whatever it was.
        console.log('[Active] Could not fetch workbook views:', err?.message);
      }
    };
    fetchWorkbookViews();
    return () => { cancelled = true; };
  }, [sessionId, activeView]);

  // Reusable function to fetch launched activities
  const fetchLaunchedActivities = useCallback(async (forceRefresh = false) => {
    if (!sessionId) return;

    // Ensure participant data exists before making API call (dual storage with auto-restore)
    const storedParticipant = tokenStorage.getParticipantData(sessionId);

    if (!storedParticipant) {
      console.warn('[Participant] No participant data in storage, skipping activities fetch');
      return;
    }

    console.log('[Participant] Fetching launched activities, forceRefresh:', forceRefresh);
    try {
      // Non-drip mode: always ensure ALL activities from all subsessions are
      // in the list. The API only returns trainer-launched activities, so we
      // supplement from session.subSessions for the full set.
      const isNonDrip = session?.sessionMode === 'non-drip';
      if (isNonDrip && session?.subSessions?.length > 0) {
        setLaunchedActivities(prev => {
          const existingIds = new Set(prev.map(a => (a._id || a.id)?.toString()));
          const allFromSubSessions = [];
          session.subSessions.forEach((ss) => {
            (ss.activities || []).forEach((act) => {
              const actId = (act._id || act.id)?.toString();
              if (actId && !existingIds.has(actId)) {
                allFromSubSessions.push({
                  ...act,
                  _id: act._id || act.id,
                  subSessionId: ss._id || ss.id,
                  subSessionName: ss.name || ss.title,
                  launchedAt: act.launchedAt || session.actualStartTime || new Date(),
                });
              }
            });
          });
          return allFromSubSessions.length > 0 ? [...prev, ...allFromSubSessions] : prev;
        });
      }

      const activitiesResponse = await participantService.getActiveActivities(sessionId);
      console.log('[Participant] Activities fetch response:', activitiesResponse);
      if (activitiesResponse.success && activitiesResponse.data) {
        const activeActivities = activitiesResponse.data;
        console.log('[Participant] Fetched activities count:', activeActivities?.length || 0);

        if (activeActivities && activeActivities.length > 0) {
          if (forceRefresh && !isNonDrip) {
            // Replace all activities when force refreshing (on navigation)
            // Only in drip mode — non-drip keeps the full subsession list
            console.log('[Participant] Force refresh - replacing all activities');
            setLaunchedActivities(activeActivities.map(act => ({
              ...act,
              launchedAt: act.launchedAt || new Date(),
            })));
          } else {
            // Merge with existing, avoiding duplicates
            setLaunchedActivities(prev => {
              const existingIds = new Set(prev.map(a => a._id || a.id));
              const newActivities = activeActivities.filter(a => !existingIds.has(a._id || a.id));
              if (newActivities.length > 0) {
                console.log('[Participant] Found new activities:', newActivities.length);
                return [...prev, ...newActivities.map(act => ({
                  ...act,
                  launchedAt: act.launchedAt || new Date(),
                }))];
              }
              // Also update existing activities with fresh data (status changes, etc.)
              return prev.map(existing => {
                const updated = activeActivities.find(a => (a._id || a.id) === (existing._id || existing.id));
                return updated ? { ...existing, ...updated, launchedAt: existing.launchedAt || updated.launchedAt || new Date() } : existing;
              });
            });
          }

          // Update responded activities
          const respondedIds = new Set();
          activeActivities.forEach(act => {
            const actId = act._id || act.id;
            if (act.hasResponded || act.userResponse || act.responded) {
              respondedIds.add(actId);
            }
            const localStorageKeys = [
              `poll_${sessionId}_${actId}`,
              `quiz_${sessionId}_${actId}`,
              `wordcloud_${sessionId}_${actId}`,
              `feedback_${sessionId}_${actId}`,
              `challenge_${sessionId}_${actId}`,
              `actionplan_${sessionId}_${actId}`,
              `openquestion_${sessionId}_${actId}`,
            ];
            const hasLocalResponse = localStorageKeys.some(key => localStorage.getItem(key));
            if (hasLocalResponse) {
              respondedIds.add(actId);
            }
          });
          if (respondedIds.size > 0) {
            setRespondedActivities(prev => new Set([...prev, ...respondedIds]));
          }
        } else if (forceRefresh) {
          // No activities - if force refresh, set empty array
          console.log('[Participant] No activities found on force refresh');
          // Don't clear - might be timing issue, let existing activities persist
        }
      }
    } catch (error) {
      console.error('[Participant] Error fetching activities:', error);
    }
  }, [sessionId]);

  // Fetch overall feedback status so the card shows even after reconnect/re-entry.
  const fetchOverallFeedbackStatus = useCallback(async () => {
    if (!sessionId) return;
    const stored = tokenStorage.getParticipantData(sessionId) || {};
    try {
      const res = await participantService.getOverallFeedbackStatus(sessionId, {
        participantId: stored.participantId,
        participantEmail: stored.email,
        participantName: stored.name,
      });
      if (res.success && res.data) {
        setOverallFeedbackStatus({
          launched: !!res.data.launched,
          endedAt: res.data.endedAt || null,
          hasSubmitted: !!res.data.hasSubmitted,
          config: res.data.config || {},
        });
        if (res.data.hasSubmitted) setOverallFeedbackSubmitted(true);
      }
    } catch (err) {
      console.error('[Participant] Error fetching overall feedback status:', err);
    }
  }, [sessionId]);

  // Refetch activities when navigating TO this page (handles mobile navigation issues)
  // This effect runs every time the location changes, ensuring activities are always fetched
  useEffect(() => {
    const currentPath = location.pathname;
    const currentKey = location.key || 'default';
    const isActivePage = currentPath.includes('/active');
    const previousPath = lastPathnameRef.current;
    const previousKey = lastLocationKeyRef.current;
    const wasOnDifferentPage = previousPath && !previousPath.includes('/active');
    const isFirstVisit = !previousPath;
    const isNewNavigation = previousKey && previousKey !== currentKey;

    console.log('[Participant] Route check - path:', currentPath, 'key:', currentKey, 'prevKey:', previousKey, 'isActive:', isActivePage, 'isFirstVisit:', isFirstVisit, 'isNewNav:', isNewNavigation);

    // Always fetch activities when landing on the active page from a different page or fresh navigation
    if (isActivePage && (wasOnDifferentPage || isFirstVisit || isNewNavigation)) {
      console.log('[Participant] Navigating to activities page - fetching activities');
      // Use a small delay to ensure component is fully mounted
      setTimeout(() => {
        fetchLaunchedActivities(true);
        fetchOverallFeedbackStatus();
      }, 100);
    }

    // Also schedule additional refetches as backup (in case of race conditions on mobile)
    if (isActivePage) {
      const timeoutId1 = setTimeout(() => {
        setLaunchedActivities(prev => {
          if (prev.length === 0) {
            console.log('[Participant] No activities after 500ms, refetching');
            fetchLaunchedActivities(true);
          }
          return prev;
        });
      }, 500);

      const timeoutId2 = setTimeout(() => {
        setLaunchedActivities(prev => {
          if (prev.length === 0) {
            console.log('[Participant] No activities after 1500ms, refetching again');
            fetchLaunchedActivities(true);
          }
          return prev;
        });
      }, 1500);

      // Update refs before cleanup
      lastPathnameRef.current = currentPath;
      lastLocationKeyRef.current = currentKey;

      return () => {
        clearTimeout(timeoutId1);
        clearTimeout(timeoutId2);
      };
    }

    // Update refs
    lastPathnameRef.current = currentPath;
    lastLocationKeyRef.current = currentKey;
  }, [location.pathname, location.key, fetchLaunchedActivities]);

  // Refresh activities when page becomes visible or window gains focus (mobile app switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sessionId) {
        console.log('[Participant] Page became visible, refreshing activities');
        fetchLaunchedActivities(true);
      }
    };

    const handleWindowFocus = () => {
      if (sessionId) {
        console.log('[Participant] Window gained focus, refreshing activities');
        fetchLaunchedActivities(true);
      }
    };

    // Mobile-specific: handle page show event (works better on iOS Safari)
    const handlePageShow = (event) => {
      if (event.persisted && sessionId) {
        console.log('[Participant] Page shown (persisted/bfcache), refreshing activities');
        fetchLaunchedActivities(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [sessionId, fetchLaunchedActivities]);

  // Refetch activities when explicitly returning to activities list view
  // This handles clicking the Aktivities nav button while already on the page
  const prevShowActivitiesListRef = useRef(showActivitiesList);
  useEffect(() => {
    // If transitioning to activities list view (from activity detail or from another page)
    if (showActivitiesList && !prevShowActivitiesListRef.current) {
      console.log('[Participant] Returned to activities list view, refreshing');
      fetchLaunchedActivities(true);
      fetchOverallFeedbackStatus();
    }
    prevShowActivitiesListRef.current = showActivitiesList;
  }, [showActivitiesList, fetchLaunchedActivities, fetchOverallFeedbackStatus]);

  // Mobile fix: Check and refetch activities if empty when on activities list view
  // This catches any edge cases where navigation doesn't trigger proper refetch
  useEffect(() => {
    if (!sessionId || !showActivitiesList) return;

    // If we're showing the activities list but have no activities, keep trying to fetch
    let attempts = 0;
    const maxAttempts = 5;

    const checkAndFetch = () => {
      setLaunchedActivities(prev => {
        if (prev.length === 0 && attempts < maxAttempts) {
          attempts++;
          console.log(`[Participant] Activities list is empty, attempt ${attempts}/${maxAttempts} to fetch`);
          fetchLaunchedActivities(true);
        }
        return prev;
      });
    };

    // Initial check after a short delay
    const timer1 = setTimeout(checkAndFetch, 300);
    const timer2 = setTimeout(checkAndFetch, 1000);
    const timer3 = setTimeout(checkAndFetch, 2000);

    // Fix #7: listen for the 'session-rehydrate' event fired by useWebSocket
    // on reconnect. Fetches fresh session state so a participant who missed
    // events while offline catches up immediately (new activity launched,
    // workbook released, session ended, etc.).
    const handleRehydrate = async (e) => {
      if (e.detail?.sessionId !== sessionId) return;
      console.log('[Participant] Re-hydrating session state after reconnect');
      try {
        const res = await participantService.getSession(sessionId);
        if (!res?.success || !res.data) return;
        const fresh = res.data;

        setSession(fresh);

        // Refresh launched activities list (picks up activities launched
        // while disconnected)
        fetchLaunchedActivities(true);

        // If session ended while we were offline, show the ended modal
        if (fresh.status === 'completed' || fresh.status === 'ended') {
          // Existing UI handles status changes via socket events; nothing
          // more to do here — the fetch above populated session state.
          console.log('[Participant] Session ended while offline');
        }
      } catch (err) {
        console.error('[Participant] Rehydrate failed:', err);
      }
    };
    window.addEventListener('session-rehydrate', handleRehydrate);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      window.removeEventListener('session-rehydrate', handleRehydrate);
    };
  }, [sessionId, showActivitiesList, fetchLaunchedActivities]);

  // WebSocket event listeners
  useEffect(() => {
    if (!socket || !sessionId) return;

    const handleActivityLaunched = (activityData) => {
      console.log('[Participant] Activity launched event received:', activityData);
      console.log('[Participant] Activity data keys:', Object.keys(activityData || {}));
      const newActivityId = activityData._id || activityData.id;
      console.log('[Participant] New activity ID:', newActivityId);
      setLaunchedActivities((prev) => {
        const exists = prev.some(a => (a._id || a.id) === newActivityId);
        if (!exists) {
          return [...prev, { ...activityData, launchedAt: new Date() }];
        }
        return prev;
      });

      setCurrentActivity(activityData);
      setShowActivitiesList(false);
      // Compute remaining from the server's launchedAt timestamp so the timer is
      // fair: late-tappers see less time, not a fresh full countdown.
      setTimeRemaining(computeInitialRemaining(activityData));

      // On relaunch: check if this participant already responded to this activity.
      // If yes, keep hasResponded=true so they see "Already Responded" instead of
      // the form again. Only participants who missed it get a fresh chance.
      const actId = activityData._id || activityData.id;
      const alreadyResponded =
        respondedActivities.has(actId) ||
        localStorage.getItem(`responded_${sessionId}_${actId}`) === 'true';

      if (alreadyResponded) {
        setHasResponded(true);
        setRespondedFromPriorRun(true);
        // Hydrate the per-activity response state from localStorage so the
        // showResults view renders with the participant's submitted data
        // (ratings, response text, etc.) instead of empty zeros after a reload.
        const actType = (activityData.type || '').toLowerCase();
        try {
          if (actType === 'feedback') {
            const saved = localStorage.getItem(`feedback_response_${sessionId}_${actId}`);
            if (saved) setFeedbackData(JSON.parse(saved));
          } else if (actType === 'poll') {
            const saved = localStorage.getItem(`poll_response_${sessionId}_${actId}`);
            if (saved) setPollResponse(JSON.parse(saved));
          } else if (actType === 'challenges') {
            const saved = localStorage.getItem(`challenge_response_${sessionId}_${actId}`);
            if (saved) setChallengeData(JSON.parse(saved));
          } else if (actType === 'actionplans') {
            const saved = localStorage.getItem(`actionplan_response_${sessionId}_${actId}`);
            if (saved) setActionPlanData(JSON.parse(saved));
          } else if (actType === 'openquestion' || actType === 'open_question') {
            const saved = localStorage.getItem(`openquestion_response_${sessionId}_${actId}`);
            if (saved) setOpenQuestionData(JSON.parse(saved));
          }
        } catch (e) {
          console.error('[Participant] Failed to hydrate response on relaunch:', e);
        }
        console.log(`[Participant] Activity ${actId} relaunched — already responded, showing submitted-state view`);
      } else {
        setHasResponded(false);
        setRespondedFromPriorRun(false);
        // Only clear form state for participants who haven't responded yet
        setPollResponse(null);
        setQuizResults(null);
        setFeedbackData(null);
        setChallengeData(null);
        setActionPlanData(null);
        setOpenQuestionData(null);
      }

      // Restore word cloud word from localStorage if previously submitted
      const savedWord = localStorage.getItem(`wordcloud_${sessionId}_${actId}`);
      setWordCloudWord(savedWord || '');

      showToast({
        title: 'New Activity Started!',
        message: activityData.title,
        icon: activityData.icon,
        color: 'blue',
        priority: 'high',
        actionUrl: `/session/${sessionId}/active`,
        actionText: 'Respond Now',
        duration: 8000,
      });
    };

    const handleWorkbookLaunched = (workbookData) => {
      console.log('Workbook launched:', workbookData);
      setLaunchedWorkbook(workbookData);
      showToast({
        title: 'Workbook Available!',
        message: workbookData.name,
        icon: '📖',
        color: 'blue',
        priority: 'medium',
        duration: 5000,
      });
    };

    const handleWorkbookStopped = () => {
      console.log('Workbook stopped');
      setLaunchedWorkbook(null);
    };

    const handleResultsUpdated = (data) => {
      console.log('[Participant] Results updated:', data);
      const activityId = data.activityId || data.id;
      const currentActivityId = currentActivity?.id || currentActivity?._id;

      if (data.results && currentActivity && (activityId === currentActivityId)) {
        // Update poll results with proper participant counts
        const updatedResults = {
          ...data.results,
          totalParticipants: data.results.totalParticipants || data.totalParticipants || data.participantCount,
          participantsVoted: data.results.participantsVoted || data.results.totalResponses || data.results.responseCount,
        };
        setPollResults(updatedResults);

        // Also update the current activity with response count
        setCurrentActivity(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            responseCount: updatedResults.participantsVoted || prev.responseCount,
            totalParticipants: updatedResults.totalParticipants || prev.totalParticipants,
          };
        });
      }
    };

    // Handle real-time responses from other participants (word cloud, polls, etc.)
    const handleResponseReceived = (data) => {
      console.log('[Participant] Response received:', data);
      const { activityId, response, participantId: responseParticipantId } = data;

      // Helper to match activity IDs (handles both string and ObjectId comparisons)
      const isCurrentActivity = currentActivity && (
        activityId === currentActivity.id ||
        activityId === currentActivity._id ||
        activityId?.toString() === currentActivity.id?.toString() ||
        activityId?.toString() === currentActivity._id?.toString()
      );

      console.log('[Participant] Activity ID match check:', {
        receivedId: activityId,
        currentId: currentActivity?.id,
        current_Id: currentActivity?._id,
        matches: isCurrentActivity
      });

      // Update the activity's responses if it's the current activity
      if (isCurrentActivity) {
        const activityType = currentActivity.type?.toLowerCase();

        // For word cloud activities, add the new word to the responses
        if ((activityType === 'word_cloud' || activityType === 'wordcloud') && response.word) {
          setCurrentActivity(prev => {
            if (!prev) return prev;
            const existingResponses = prev.responses || prev.wordCloud || prev.words || [];
            // Check if this participant already submitted (allow same word from different participants)
            const responseExists = existingResponses.some(r =>
              r.participantId === responseParticipantId
            );
            if (responseExists) return prev;

            const newResponse = {
              word: response.word,
              participantId: responseParticipantId,
              timestamp: data.timestamp
            };
            return {
              ...prev,
              responses: [...existingResponses, newResponse],
              wordCloud: [...existingResponses, newResponse],
              words: [...existingResponses, newResponse]
            };
          });
        }

        // For poll activities, update the poll results
        if (activityType === 'poll' && (response.optionIndex !== undefined || response.value !== undefined)) {
          console.log('[Poll] Processing poll response from participant:', responseParticipantId);
          // Parse optionIndex as number to handle string values
          let optionIndex = response.optionIndex !== undefined ? response.optionIndex : response.value;
          if (typeof optionIndex === 'string') {
            optionIndex = parseInt(optionIndex, 10);
          }
          if (typeof optionIndex !== 'number' || isNaN(optionIndex)) {
            console.warn('[Poll] Invalid optionIndex:', response.optionIndex, response.value);
            return;
          }

          // Update current activity with vote count - check for duplicates
          setCurrentActivity(prev => {
            if (!prev) return prev;
            const existingResponses = prev.responses || [];
            // Check if this participant already voted
            const hasAlreadyVoted = existingResponses.some(r => r.participantId === responseParticipantId);
            if (hasAlreadyVoted) {
              console.log('[Poll] Duplicate vote from participant ignored:', responseParticipantId);
              return prev;
            }

            const options = [...(prev.config?.options || prev.options || [])];
            if (options[optionIndex]) {
              if (typeof options[optionIndex] === 'object') {
                options[optionIndex] = {
                  ...options[optionIndex],
                  votes: (options[optionIndex].votes || 0) + 1
                };
              }
            }
            console.log('[Poll] Adding vote for option', optionIndex, 'from participant:', responseParticipantId);
            return {
              ...prev,
              config: { ...prev.config, options },
              options,
              responses: [...existingResponses, { participantId: responseParticipantId, optionIndex, timestamp: data.timestamp }],
              responseCount: (prev.responseCount || 0) + 1,
            };
          });

          // Also update pollResults state - check for duplicates using votedParticipants Set
          setPollResults(prev => {
            // Check if this participant already voted using votedParticipants set
            const votedParticipants = new Set(prev?.votedParticipants || []);
            if (votedParticipants.has(responseParticipantId)) {
              console.log('[Poll] Duplicate vote in pollResults ignored for participant:', responseParticipantId);
              return prev;
            }

            console.log('[Poll] Updating pollResults with vote for option', optionIndex);
            if (!prev) {
              // Initialize with the vote
              const votes = new Array(currentActivity?.config?.options?.length || 4).fill(0);
              votes[optionIndex] = 1;
              return {
                votes,
                totalVotes: 1,
                participantsVoted: 1,
                totalParticipants: currentActivity?.totalParticipants || 1,
                votedParticipants: [responseParticipantId],
              };
            }
            const updatedVotes = [...(prev.votes || [])];
            if (optionIndex < updatedVotes.length) {
              updatedVotes[optionIndex] = (updatedVotes[optionIndex] || 0) + 1;
            }
            const newPollResults = {
              ...prev,
              votes: updatedVotes,
              totalVotes: (prev.totalVotes || 0) + 1,
              participantsVoted: (prev.participantsVoted || 0) + 1,
              votedParticipants: [...(prev.votedParticipants || []), responseParticipantId],
            };
            console.log('[Poll] Updated pollResults:', newPollResults);
            return newPollResults;
          });
        }
      } else {
        console.log('[Poll] Response received but not for current activity');
      }

      // Also update the activity in launchedActivities list - check for duplicates
      setLaunchedActivities(prev =>
        prev.map(activity => {
          if (activity.id === activityId || activity._id === activityId) {
            const existingResponses = activity.responses || activity.wordCloud || activity.words || [];
            const actType = activity.type?.toLowerCase();
            const newResponse = { ...response, participantId: responseParticipantId, timestamp: data.timestamp };

            // Check if this participant already submitted a response for this activity
            const hasAlreadyResponded = existingResponses.some(r => r.participantId === responseParticipantId);
            if (hasAlreadyResponded) {
              console.log('[Activity] Duplicate response ignored for participant:', responseParticipantId);
              return activity;
            }

            // For word cloud, update all three arrays for consistency
            if (actType === 'word_cloud' || actType === 'wordcloud') {
              return {
                ...activity,
                responses: [...existingResponses, newResponse],
                wordCloud: [...existingResponses, newResponse],
                words: [...existingResponses, newResponse],
                responseCount: (activity.responseCount || existingResponses.length) + 1
              };
            }

            return {
              ...activity,
              responses: [...existingResponses, newResponse],
              responseCount: (activity.responseCount || existingResponses.length) + 1
            };
          }
          return activity;
        })
      );
    };

    const handleActivityClosed = (data) => {
      console.log('[Participant] Activity closed:', data);
      const activityId = data.activityId || data.id;

      // Update the activity status in launchedActivities
      setLaunchedActivities((prev) =>
        prev.map(activity =>
          (activity.id === activityId || activity._id === activityId)
            ? { ...activity, status: 'completed' }
            : activity
        )
      );

      // If currently viewing this activity, mark it as completed
      if (currentActivity && (currentActivity.id === activityId || currentActivity._id === activityId)) {
        setCurrentActivity(prev => prev ? { ...prev, status: 'completed' } : null);
      }

      showToast({
        type: 'warning',
        title: 'Activity Ended',
        message: data.title || 'The trainer has closed this activity',
        color: 'orange',
        duration: 4000,
      });
    };

    const handleSessionEnded = (data) => {
      console.log('[Participant] Session ended:', data);

      // Prevent duplicate handling (multiple events can fire)
      if (sessionEndedHandledRef.current) {
        console.log('[Participant] Session end already handled, skipping');
        return;
      }
      sessionEndedHandledRef.current = true;

      // Update session status to completed
      setSession(prev => prev ? { ...prev, status: 'completed' } : prev);

      // Mark all activities as completed
      setLaunchedActivities(prev => prev.map(act => ({
        ...act,
        status: 'completed',
      })));

      setSessionEndReason(data?.reason || 'The trainer has ended this session');
      setShowSessionEndedModal(true);
      // Only show modal, no toast to avoid duplicate notifications
    };

    // Session paused - for multi-day sessions
    const handleSessionPaused = (data) => {
      console.log('[Participant] Session paused:', data);
      setSession(prev => prev ? { ...prev, status: 'paused' } : prev);
      setShowSessionPausedModal(true);
    };

    const handleSessionResumed = (data) => {
      console.log('[Participant] Session resumed:', data);
      setSession(prev => prev ? { ...prev, status: 'live' } : prev);
      setShowSessionPausedModal(false);
    };

    const handleSubsessionEnded = (data) => {
      console.log('[Participant] Subsession ended:', data);

      // If there's a break time, show the break modal
      if (data?.hasBreak && data?.breakTime > 0) {
        setBreakInfo({
          name: data.name || 'Current Section',
          breakTime: data.breakTime,
        });
        setShowBreakModal(true);
      } else {
        showToast({
          title: 'Section Ended',
          message: data?.name ? `"${data.name}" has ended` : 'The current section has ended',
          icon: '📋',
          color: 'blue',
          duration: 5000,
        });
      }
    };

    const handleSubsessionStarted = (data) => {
      console.log('[Participant] Subsession started:', data);
      // Close break modal if open
      setShowBreakModal(false);
      // Show subsession started modal
      setSubsessionStartedInfo({
        name: data?.name || 'Next Section',
        number: data?.number || 0,
      });
      setShowSubsessionStartedModal(true);
    };

    socket.on('activity:launched', handleActivityLaunched);
    socket.on('workbook:launched', handleWorkbookLaunched);
    socket.on('workbook:stopped', handleWorkbookStopped);
    socket.on('results-updated', handleResultsUpdated);
    socket.on('response-received', handleResponseReceived);
    socket.on('activity:closed', handleActivityClosed);
    socket.on('activity-ended', handleActivityClosed);
    socket.on('session:ended', handleSessionEnded);
    socket.on('session:paused', handleSessionPaused);
    socket.on('session:resumed', handleSessionResumed);
    socket.on('session-status-changed', (data) => {
      if (data.status === 'ended' || data.status === 'completed') {
        handleSessionEnded(data);
      } else if (data.status === 'paused') {
        handleSessionPaused(data);
      } else if (data.status === 'live') {
        handleSessionResumed(data);
      }
    });
    socket.on('subsession:ended', handleSubsessionEnded);
    socket.on('subsession:started', handleSubsessionStarted);

    // Overall session feedback events
    const handleOverallFeedbackLaunched = (data) => {
      console.log('[Participant] Overall session feedback launched');
      setOverallFeedbackStatus(prev => ({
        ...prev,
        launched: true,
        endedAt: null,
        config: data?.config || prev.config || {},
      }));
      if (!overallFeedbackSubmitted) setShowOverallFeedbackModal(true);
    };

    const handleOverallFeedbackEnded = () => {
      console.log('[Participant] Overall session feedback ended');
      setOverallFeedbackStatus(prev => ({ ...prev, endedAt: new Date().toISOString() }));
      setShowOverallFeedbackModal(false);
    };

    socket.on('overallFeedback:launched', handleOverallFeedbackLaunched);
    socket.on('overallFeedback:ended', handleOverallFeedbackEnded);

    // Trainer changed the running activity's timer (pause / resume / extend / stop).
    // Merge the new state into currentActivity so the countdown + UI respond, and
    // recompute timeRemaining from the new launchedAt/timeLimit. When the trainer
    // hits "Stop Timer", timerEnabled flips to false and the timer UI hides.
    const handleTimerUpdated = (data) => {
      if (!data?.activityId) return;
      setCurrentActivity(prev => {
        const currentId = prev?._id?.toString() || prev?.id?.toString();
        if (!prev || currentId !== data.activityId.toString()) return prev;
        const nextActivity = {
          ...prev,
          launchedAt: data.launchedAt || prev.launchedAt,
          timeLimit: (typeof data.timeLimit === 'number') ? data.timeLimit : prev.timeLimit,
          timerEnabled: (typeof data.timerEnabled === 'boolean') ? data.timerEnabled : prev.timerEnabled,
          timerPausedAt: data.timerPausedAt || null,
        };
        // Sync the local countdown to the new deadline. When paused, freeze at
        // remaining = (launchedAt + timeLimit - pausedAt); otherwise compute normally.
        if (nextActivity.timerEnabled === false || !nextActivity.timeLimit) {
          setTimeRemaining(0);
        } else {
          const launchTs = nextActivity.launchedAt ? new Date(nextActivity.launchedAt).getTime() : Date.now();
          const pauseTs = nextActivity.timerPausedAt ? new Date(nextActivity.timerPausedAt).getTime() : null;
          const evalTs = pauseTs || Date.now();
          setTimeRemaining(Math.max(0, Math.round((launchTs + nextActivity.timeLimit * 1000 - evalTs) / 1000)));
        }
        return nextActivity;
      });
    };
    socket.on('activity:timer-updated', handleTimerUpdated);

    // Handle participant joined event - update session participant count
    const handleParticipantJoined = (data) => {
      console.log('[Participant] New participant joined:', data);
      if (data.sessionId === sessionId || data.participantCount !== undefined) {
        // Update session state with new participant count
        setSession(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            analytics: {
              ...prev.analytics,
              totalParticipants: data.totalParticipants || data.participantCount,
              peakParticipants: data.peakParticipants || prev.analytics?.peakParticipants,
            },
          };
        });
      }
    };

    socket.on('participant-joined', handleParticipantJoined);

    // Handle being removed by trainer
    const handleRemoved = (data) => {
      const storedParticipant = tokenStorage.getParticipantData(sessionId);
      if (storedParticipant?.participantId === data.participantId) {
        alert('You have been removed from this session by the trainer.');
        navigate('/join', { replace: true });
      }
    };
    socket.on('participant:removed', handleRemoved);

    return () => {
      socket.off('activity:launched', handleActivityLaunched);
      socket.off('workbook:launched', handleWorkbookLaunched);
      socket.off('workbook:stopped', handleWorkbookStopped);
      socket.off('results-updated', handleResultsUpdated);
      socket.off('response-received', handleResponseReceived);
      socket.off('activity:closed', handleActivityClosed);
      socket.off('activity-ended', handleActivityClosed);
      socket.off('session:ended', handleSessionEnded);
      socket.off('session:paused', handleSessionPaused);
      socket.off('session:resumed', handleSessionResumed);
      socket.off('session-status-changed');
      socket.off('subsession:ended', handleSubsessionEnded);
      socket.off('subsession:started', handleSubsessionStarted);
      socket.off('overallFeedback:launched', handleOverallFeedbackLaunched);
      socket.off('overallFeedback:ended', handleOverallFeedbackEnded);
      socket.off('activity:timer-updated', handleTimerUpdated);
      socket.off('participant-joined', handleParticipantJoined);
      socket.off('participant:removed', handleRemoved);
    };
  }, [socket, sessionId, showToast, currentActivity]);

  // Timer countdown — only runs when the activity has a timer configured and isn't paused.
  // Timer-disabled activities run indefinitely until the trainer stops them.
  useEffect(() => {
    const isPaused = !!currentActivity?.timerPausedAt;
    if (!hasResponded && currentActivity && hasTimer(currentActivity) && !isPaused && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => (prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [hasResponded, currentActivity, timeRemaining]);

  // Persist view state to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(`participant_view_${sessionId}`, showActivitiesList ? 'list' : 'activity');
    } catch {
      // Ignore sessionStorage errors
    }
  }, [showActivitiesList, sessionId]);

  // NOTE: Removed auto-progression after responding (was 5 seconds)
  // Participants should be able to view their responses for as long as they want
  // They can manually navigate back using the "Back to Activities" button

  // Q&A auto-progress when timer expires
  useEffect(() => {
    if (currentActivity?.type === 'qna' && timeRemaining === 0 && !hasResponded) {
      const actId = currentActivity._id || currentActivity.id;
      const timer = setTimeout(() => {
        setShowActivitiesList(true);
        setCurrentActivity(null);
        setRespondedActivities((prev) => new Set([...prev, actId]));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [currentActivity, timeRemaining, hasResponded]);

  // Helper function to emit response via WebSocket
  const emitResponse = (activityId, response) => {
    if (socket) {
      // Get participant data from dual storage (auto-restores from sessionStorage if purged)
      const participantData = tokenStorage.getParticipantData(sessionId) || {};

      // Use _id if available (for database activities), otherwise use id
      const id = currentActivity?._id || activityId;

      const enrichedResponse = {
        ...response,
        participantId: participantData.participantId, // Unique participant ID from backend
        participantEmail: participantData.email,
        participantName: participantData.name,
        activityTitle: currentActivity?.title || currentActivity?.name,
      };

      socket.emit('submit-response', {
        sessionId,
        activityId: id,
        participantId: participantData.participantId, // Also send at top level
        participantName: participantData.name,
        response: enrichedResponse,
      });
      console.log('[Participant] Response submitted via WebSocket:', { activityId: id, participantId: participantData.participantId, response: enrichedResponse });
    }
  };

  // Activity submission handlers
  const handlePollSubmit = (response) => {
    setPollResponse(response);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      // Put spread BEFORE type to prevent response.type ('single'/'multiple') from overwriting 'poll'
      emitResponse(actId, { ...response, type: 'poll' });
      // Save responded state and response to localStorage for persistence
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
      localStorage.setItem(`poll_response_${sessionId}_${actId}`, JSON.stringify(response));
    }
    showToast({ title: 'Vote Submitted!', message: 'Your vote has been recorded', icon: '✓', color: 'green' });
  };

  const handleQuizSubmit = (response) => {
    // Store both results and answers for displaying in the review section
    setQuizResults({ ...response.results, answers: response.answers });
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'quiz', ...response });
      // Save responded state to localStorage for persistence
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
    }
    showToast({
      title: 'Quiz Submitted!',
      message: `You scored ${response.results.percentage}%`,
      icon: response.results.passed ? '🎉' : '📝',
      color: response.results.passed ? 'green' : 'orange',
    });
  };

  const handleWordCloudSubmit = (response) => {
    setWordCloudWord(response.word);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'word_cloud', word: response.word });
      // Save word and responded state to localStorage for persistence
      localStorage.setItem(`wordcloud_${sessionId}_${actId}`, response.word);
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
    }
    showToast({ title: 'Word Submitted!', message: `Your word: "${response.word}"`, icon: '☁️', color: 'green' });
  };

  const handleQAQuestionSubmitted = (question) => {
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'qna', question });
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
    }
    showToast({ title: 'Question Submitted!', message: 'Your question has been added', icon: '🙋', color: 'green' });
  };

  const handleFeedbackSubmit = (response) => {
    setFeedbackData(response);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'feedback', ...response });
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
      // Save response to localStorage for persistence
      localStorage.setItem(`feedback_response_${sessionId}_${actId}`, JSON.stringify(response));
    }
    showToast({ title: 'Feedback Submitted!', message: 'Thank you for your feedback', icon: '🙏', color: 'green' });
  };

  const handleChallengeSubmit = (response) => {
    setChallengeData(response);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'challenges', ...response });
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
      // Save response to localStorage for persistence
      localStorage.setItem(`challenge_response_${sessionId}_${actId}`, JSON.stringify(response));
    }
    showToast({ title: 'Challenge Submitted!', message: 'Your trainer will review it', icon: '⚠️', color: 'orange' });
  };

  const handleActionPlanSubmit = (response) => {
    setActionPlanData(response);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'actionplans', ...response });
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
      // Save response to localStorage for persistence
      localStorage.setItem(`actionplan_response_${sessionId}_${actId}`, JSON.stringify(response));
    }
    showToast({ title: 'Action Plan Submitted!', message: 'Great job planning ahead', icon: '📋', color: 'blue' });
  };

  const handleOpenQuestionSubmit = (response) => {
    setOpenQuestionData(response);
    setHasResponded(true);
    if (currentActivity) {
      const actId = getActivityId(currentActivity);
      setRespondedActivities((prev) => new Set([...prev, actId]));
      emitResponse(actId, { type: 'openquestion', ...response });
      localStorage.setItem(`responded_${sessionId}_${actId}`, 'true');
      // Save response to localStorage for persistence
      localStorage.setItem(`openquestion_response_${sessionId}_${actId}`, JSON.stringify(response));
    }
    showToast({ title: 'Response Submitted!', message: 'Your answer has been recorded', icon: '✍️', color: 'green' });
  };

  // Submit overall session feedback - 5 questions format
  const handleSubmitOverallFeedback = async () => {
    // Check if all questions are answered
    const allAnswered = Object.values(overallFeedbackRatings).every(r => r > 0);
    if (!allAnswered) {
      showToast({ title: 'Please answer all questions', message: 'Rate all 5 questions to submit', icon: '⭐', color: 'yellow' });
      return;
    }

    try {
      const participantData = tokenStorage.getParticipantData(sessionId) || {};
      // Calculate average rating for backwards compatibility
      const avgRating = Math.round(Object.values(overallFeedbackRatings).reduce((sum, r) => sum + r, 0) / 5);

      const response = await participantService.submitOverallFeedback(sessionId, {
        participantId: participantData.participantId || participantId,
        participantName: participantData.name || participantName,
        participantEmail: participantData.email || '',
        // New 5-question format
        topicRelevance: overallFeedbackRatings.topicRelevance,
        trainerEffectiveness: overallFeedbackRatings.trainerEffectiveness,
        confidenceToApply: overallFeedbackRatings.confidenceToApply,
        learningAdvocacy: overallFeedbackRatings.learningAdvocacy,
        challengeResolution: overallFeedbackRatings.challengeResolution,
        comment: overallFeedbackComment,
        // Legacy field for backwards compatibility
        rating: avgRating,
      });

      if (response.success) {
        setOverallFeedbackSubmitted(true);
        setOverallFeedbackStatus(prev => ({ ...prev, hasSubmitted: true }));
        showToast({ title: 'Feedback Submitted!', message: 'Thank you for your feedback', icon: '🎉', color: 'green' });
      } else {
        showToast({ title: 'Error', message: response.error || 'Failed to submit feedback', icon: '❌', color: 'red' });
      }
    } catch (error) {
      console.error('Error submitting overall feedback:', error);
      showToast({ title: 'Error', message: 'Failed to submit feedback', icon: '❌', color: 'red' });
    }
  };

  const handleStartActivity = (activity) => {
    const actId = getActivityId(activity);

    // Check multiple sources to determine if user has responded
    const inMemoryResponded = respondedActivities.has(actId);
    const activityResponded = activity.hasResponded || activity.userResponse || activity.responded;
    const responseInActivity = activity.responses && activity.responses.some(r =>
      r.participantId === participantId || r.participantName === participantName
    );
    const localStorageResponded = localStorage.getItem(`responded_${sessionId}_${actId}`) === 'true';

    const isResponded = inMemoryResponded || activityResponded || responseInActivity || localStorageResponded;
    const isCompleted = activity.status === 'completed' || activity.status === 'closed';

    setCurrentActivity(activity);
    setShowActivitiesList(false);
    // Only mark as responded if user actually responded (not just because activity ended)
    // This allows "Activity Ended - Not Attended" view to show correctly
    setHasResponded(isResponded);
    // If they previously responded and the activity is still live (i.e. it
    // got relaunched while they had this page in the background), surface
    // the simple "already responded" view instead of the form/results.
    setRespondedFromPriorRun(isResponded && !isCompleted);
    // timeLimit is in seconds, duration is in minutes (convert to seconds)
    // Compute remaining from the server's launchedAt timestamp.
    setTimeRemaining(isCompleted ? 0 : computeInitialRemaining(activity));

    if (!isResponded && !isCompleted) {
      setPollResponse(null);
      setQuizResults(null);
      setFeedbackData(null);
      setChallengeData(null);
      setActionPlanData(null);
      setOpenQuestionData(null);
    } else {
      // Load saved responses for previously responded activities
      const activityType = activity.type?.toLowerCase();

      // Try to get user's response from activity data first, then localStorage
      if (activityType === 'poll') {
        // Check activity.userResponse first (from server)
        if (activity.userResponse) {
          setPollResponse(activity.userResponse.response || activity.userResponse);
        } else {
          // Try localStorage as fallback
          const savedPollResponse = localStorage.getItem(`poll_response_${sessionId}_${actId}`);
          if (savedPollResponse) {
            try {
              setPollResponse(JSON.parse(savedPollResponse));
            } catch (e) {
              console.error('Error parsing saved poll response:', e);
            }
          }
        }

        // Use poll results from backend if available (aggregated vote counts)
        if (activity.pollResults) {
          console.log('[Poll] Using backend pollResults:', activity.pollResults);
          setPollResults({
            ...activity.pollResults,
            totalParticipants: activity.totalParticipants || activity.participantCount || activity.pollResults.participantsVoted || 1,
          });
        } else if (activity.responses?.length > 0) {
          // Fallback: Calculate poll results from activity responses data if available
          const responses = activity.responses || [];
          const options = activity.config?.options || activity.options || [];
          const optionsCount = options.length || 4;
          const votes = new Array(optionsCount).fill(0);
          const votedParticipants = new Set();

          // Count votes from each response
          responses.forEach(r => {
            const pId = r.participantId || r.participant;
            if (pId && !votedParticipants.has(pId)) {
              votedParticipants.add(pId);
              // Handle different response formats
              let optionIndex = r.optionIndex ?? r.selectedOption ?? r.response?.value ?? r.response?.single ?? r.value ?? r.single;
              // Convert string to number if needed
              if (typeof optionIndex === 'string') {
                optionIndex = parseInt(optionIndex, 10);
              }
              if (typeof optionIndex === 'number' && !isNaN(optionIndex) && optionIndex >= 0 && optionIndex < votes.length) {
                votes[optionIndex]++;
              }
              // Handle multiple choice
              const multipleOptions = r.selectedOptions ?? r.response?.value ?? r.response?.multiple;
              if (Array.isArray(multipleOptions)) {
                multipleOptions.forEach(idx => {
                  let parsedIdx = idx;
                  if (typeof idx === 'string') {
                    parsedIdx = parseInt(idx, 10);
                  }
                  if (typeof parsedIdx === 'number' && !isNaN(parsedIdx) && parsedIdx >= 0 && parsedIdx < votes.length) {
                    votes[parsedIdx]++;
                  }
                });
              }
            }
          });

          const totalVotes = votes.reduce((sum, v) => sum + v, 0);
          if (totalVotes > 0 || responses.length > 0) {
            console.log('[Poll] Calculated pollResults from responses:', { votes, totalVotes });
            setPollResults({
              votes,
              totalVotes,
              participantsVoted: votedParticipants.size || responses.length,
              totalParticipants: activity.totalParticipants || activity.participantCount || votedParticipants.size || 1,
              votedParticipants: Array.from(votedParticipants),
              options: options.map(o => typeof o === 'string' ? o : o?.text || o?.label || 'Option'),
            });
          }
        }
      } else if (activityType === 'quiz') {
        if (activity.userResponse?.results) {
          setQuizResults(activity.userResponse.results);
        }
      } else if (activityType === 'feedback') {
        if (activity.userResponse) {
          setFeedbackData(activity.userResponse.response || activity.userResponse);
        } else {
          // Try localStorage as fallback
          const savedFeedback = localStorage.getItem(`feedback_response_${sessionId}_${actId}`);
          if (savedFeedback) {
            try {
              setFeedbackData(JSON.parse(savedFeedback));
            } catch (e) {
              console.error('Error parsing saved feedback response:', e);
            }
          }
        }
      } else if (activityType === 'challenges') {
        if (activity.userResponse) {
          setChallengeData(activity.userResponse.response || activity.userResponse);
        } else {
          // Try localStorage as fallback
          const savedChallenge = localStorage.getItem(`challenge_response_${sessionId}_${actId}`);
          if (savedChallenge) {
            try {
              setChallengeData(JSON.parse(savedChallenge));
            } catch (e) {
              console.error('Error parsing saved challenge response:', e);
            }
          }
        }
      } else if (activityType === 'actionplans' || activityType === 'actionplan') {
        if (activity.userResponse) {
          setActionPlanData(activity.userResponse.response || activity.userResponse);
        } else {
          // Try localStorage as fallback
          const savedActionPlan = localStorage.getItem(`actionplan_response_${sessionId}_${actId}`);
          if (savedActionPlan) {
            try {
              setActionPlanData(JSON.parse(savedActionPlan));
            } catch (e) {
              console.error('Error parsing saved action plan response:', e);
            }
          }
        }
      } else if (activityType === 'openquestion' || activityType === 'open_question') {
        if (activity.userResponse) {
          setOpenQuestionData(activity.userResponse.response || activity.userResponse);
        } else {
          // Try localStorage as fallback
          const savedOpenQuestion = localStorage.getItem(`openquestion_response_${sessionId}_${actId}`);
          if (savedOpenQuestion) {
            try {
              setOpenQuestionData(JSON.parse(savedOpenQuestion));
            } catch (e) {
              console.error('Error parsing saved open question response:', e);
            }
          }
        }
      }
    }
    // Always try to restore word cloud word from localStorage
    const savedWord = localStorage.getItem(`wordcloud_${sessionId}_${actId}`);
    if (savedWord) {
      setWordCloudWord(savedWord);
    } else if (!isResponded && !isCompleted) {
      setWordCloudWord('');
    }
  };

  const handleBackToList = () => {
    setShowActivitiesList(true);
    setCurrentActivity(null);
    setRespondedFromPriorRun(false);
  };

  // Helper function to get consistent activity ID (handles both _id and id)
  const getActivityId = (activity) => activity?._id || activity?.id;


  // Render activity component based on type
  const renderActivity = () => {
    const isActivityCompleted = currentActivity.status === 'completed';
    const isActivityEnded = ['completed', 'closed', 'ended'].includes(currentActivity.status);
    // Normalize activity type to lowercase for consistent matching
    const activityType = currentActivity.type?.toLowerCase();

    // Previously short-circuited to a generic "already responded" card on
    // relaunch. Removed so the activity's own showResults view renders with
    // the participant's submitted ratings/response.
    if (false && respondedFromPriorRun && !isActivityEnded) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-6">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-8 max-w-md w-full text-center">
            <div className="mx-auto mb-4 w-14 h-14 rounded-full bg-emerald-50 ring-1 ring-inset ring-emerald-100 flex items-center justify-center">
              <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-1">You've already responded</h3>
            <p className="text-sm text-neutral-500 leading-relaxed mb-6">
              Your response for <span className="font-medium text-neutral-700">{currentActivity.title || currentActivity.name || 'this activity'}</span> has been recorded.
              The trainer relaunched it for participants who hadn't answered yet — you don't need to submit again.
            </p>
            <Button variant="ghost" onClick={handleBackToList}>Back to Activities</Button>
          </div>
        </div>
      );
    }

    switch (activityType) {
      case 'poll':
        return (
          <PollActivity
            activity={{
              ...currentActivity,
              totalParticipants: session.currentParticipants || session.maxParticipants || 1,
            }}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handlePollSubmit}
            onNavigateToWorkbooks={() => navigate(`/session/${sessionId}/workbooks`)}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userResponse={pollResponse}
            results={pollResults}
            isActivityCompleted={isActivityCompleted}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'quiz':
        return (
          <QuizActivity
            // Remount per quiz: without a key React reuses the instance across
            // launches, carrying over the previous quiz's selected answers and
            // question index (a 1-question quiz opened on index 3 crashed).
            key={currentActivity._id || currentActivity.id}
            activity={currentActivity}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleQuizSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            quizResults={quizResults}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'word_cloud':
      case 'wordcloud':
        return (
          <WordCloudActivity
            activity={currentActivity}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleWordCloudSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userWord={wordCloudWord}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'qna':
      case 'qa':
        return (
          <QAActivity
            activity={currentActivity}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            initialQuestions={qaQuestions}
            onQuestionSubmitted={handleQAQuestionSubmitted}
            onBackToList={handleBackToList}
          />
        );
      case 'feedback':
        return (
          <FeedbackActivity
            activity={currentActivity}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleFeedbackSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userFeedback={feedbackData}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'challenges':
        return (
          <ChallengesActivity
            activity={currentActivity}
            sessionId={sessionId}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleChallengeSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userChallenge={challengeData}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'actionplans':
        return (
          <ActionPlansActivity
            activity={currentActivity}
            sessionId={sessionId}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleActionPlanSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userActionPlan={actionPlanData}
            isActivityEnded={isActivityEnded}
          />
        );
      case 'openquestion':
      case 'open_question':
        return (
          <OpenQuestionActivity
            activity={currentActivity}
            sessionCode={session.code}
            sessionName={session.name}
            trainerName={session.trainer?.name || session.trainerName || 'Trainer'}
            timeRemaining={timeRemaining}
            onSubmit={handleOpenQuestionSubmit}
            onBackToList={handleBackToList}
            showResults={hasResponded}
            userResponse={openQuestionData}
            isActivityEnded={isActivityEnded}
          />
        );
      default:
        return (
          <div className="flex items-center justify-center p-8">
            <div className="bg-white rounded-xl border border-neutral-200 p-8 text-center">
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">Unknown Activity Type</h3>
              <p className="text-neutral-500 mb-4">Activity type "{currentActivity.type}" is not supported.</p>
              <Button onClick={handleBackToList}>Back to Activities</Button>
            </div>
          </div>
        );
    }
  };

  if (!session) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  // Render modals - shared between both views
  const renderModals = () => (
    <>
      {/* Session Ended Modal */}
      <Modal
        isOpen={showSessionEndedModal}
        onClose={() => {
          setShowSessionEndedModal(false);
          if (authService.isAuthenticated()) {
            navigate('/participant/dashboard');
          } else {
            navigate('/join');
          }
        }}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-orange-100 mb-5">
            <svg className="h-10 w-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Ended</h3>
          <p className="text-base text-neutral-600 mb-6 leading-relaxed">
            {sessionEndReason || 'The trainer has ended this session. Thank you for participating!'}
          </p>
          {session && (
            <div className="bg-neutral-50 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-neutral-900">{session.name}</p>
              <p className="text-xs text-neutral-500 mt-1">Session Code: {session.code}</p>
            </div>
          )}
          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setShowSessionEndedModal(false);
                navigate(`/session/${sessionId}/reports`);
              }}
              fullWidth
              size="lg"
            >
              View My Report
            </Button>
            <Button
              variant="ghost"
              onClick={() => {
                setShowSessionEndedModal(false);
                if (authService.isAuthenticated()) {
                  navigate('/participant/dashboard');
                } else {
                  navigate('/join');
                }
              }}
              fullWidth
            >
              {authService.isAuthenticated() ? 'Return to Dashboard' : 'Join Another Session'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Session Paused Modal - For multi-day sessions */}
      <Modal
        isOpen={showSessionPausedModal}
        onClose={() => setShowSessionPausedModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-purple-100 mb-5">
            <svg className="h-10 w-10 text-purple-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Paused for Today</h3>
          <p className="text-base text-neutral-600 mb-6 leading-relaxed">
            The trainer has paused this session for today. The session will continue tomorrow from where you left off.
          </p>

          {/* Participant Code Reminder */}
          {participantCode && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 mb-6">
              <p className="text-sm font-semibold text-purple-900 mb-3">Your Participant Code</p>
              <div className="flex items-center justify-center gap-3">
                <span className="text-2xl font-mono font-bold text-purple-700 tracking-wider bg-white px-4 py-2 rounded-lg border border-purple-300">
                  {participantCode}
                </span>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(participantCode);
                      setPausedCodeCopied(true);
                      setTimeout(() => setPausedCodeCopied(false), 2000);
                    } catch (err) {
                      console.error('Failed to copy code:', err);
                    }
                  }}
                  className={`p-2.5 rounded-lg transition-all ${
                    pausedCodeCopied
                      ? 'bg-green-100 text-green-700'
                      : 'bg-white border border-purple-300 text-purple-600 hover:bg-purple-100'
                  }`}
                  title={pausedCodeCopied ? 'Copied!' : 'Copy code'}
                >
                  {pausedCodeCopied ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </div>
              <p className="text-xs text-purple-700 mt-3">
                Use this code along with session code <span className="font-bold">{session?.code}</span> to rejoin tomorrow
              </p>
            </div>
          )}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <p className="text-sm text-amber-800 text-left">
                <span className="font-semibold">Important:</span> Save your participant code! You will need it to continue from where you left off tomorrow.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <Button
              variant="primary"
              onClick={() => {
                setShowSessionPausedModal(false);
                navigate(`/session/${sessionId}/reports`);
              }}
              fullWidth
              size="lg"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              View My Progress
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowSessionPausedModal(false);
                navigate(`/session/${sessionId}/content`);
              }}
              fullWidth
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Browse Content & Workbooks
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowSessionPausedModal(false)}
              fullWidth
            >
              Stay in Session
            </Button>
          </div>
        </div>
      </Modal>

      {/* Overall Session Feedback Modal - 5 Questions Format */}
      <Modal
        isOpen={showOverallFeedbackModal && !overallFeedbackSubmitted}
        onClose={() => {}}
        title=""
        size="lg"
      >
        <div className="py-4 px-2 sm:px-4">
          <div className="text-center mb-6">
            <div className="mx-auto inline-flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-neutral-900 mb-2">Session Complete!</h3>
            <p className="text-sm text-neutral-600">
              Please rate your overall experience with this session.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-4">
            <div className="flex items-center justify-between text-xs text-neutral-500 mb-1">
              <span>{Object.values(overallFeedbackRatings).filter(r => r > 0).length} of 5 answered</span>
            </div>
            <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-300"
                style={{ width: `${(Object.values(overallFeedbackRatings).filter(r => r > 0).length / 5) * 100}%` }}
              />
            </div>
          </div>

          {/* 5 Questions with Emoji Ratings.
              No inner scroll — the Modal body is the single scroll container.
              Nesting a second scrollable area here caused iOS Safari to hijack
              taps near the Submit button (the page jumped instead of clicking). */}
          <div className="space-y-3 mb-4">
            {[
              { key: 'topicRelevance', question: 'How relevant and useful was this training for your role?' },
              { key: 'trainerEffectiveness', question: 'How effectively did the trainer help you understand and engage?' },
              { key: 'confidenceToApply', question: 'How confident do you feel about applying what you learned?' },
              { key: 'learningAdvocacy', question: 'How likely are you to apply this learning and recommend it?' },
              { key: 'challengeResolution', question: 'To what extent did this training address your real challenges?' },
            ].map((q, index) => (
              <div key={q.key} className={`bg-neutral-50 rounded-lg p-3 ${overallFeedbackRatings[q.key] > 0 ? 'border-l-4 border-green-500' : ''}`}>
                <div className="flex items-start gap-2 mb-2">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold ${
                    overallFeedbackRatings[q.key] > 0 ? 'bg-green-500 text-white' : 'bg-neutral-200 text-neutral-600'
                  }`}>
                    {overallFeedbackRatings[q.key] > 0 ? '✓' : index + 1}
                  </div>
                  <p className="text-sm text-neutral-800">{q.question}</p>
                </div>
                <div className="flex justify-center gap-1">
                  {[
                    { value: 1, emoji: '😞', label: 'Poor' },
                    { value: 2, emoji: '😑', label: 'Fair' },
                    { value: 3, emoji: '🙂', label: 'Okay' },
                    { value: 4, emoji: '😄', label: 'Good' },
                    { value: 5, emoji: '🤩', label: 'Excellent' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setOverallFeedbackRatings(prev => ({ ...prev, [q.key]: option.value }))}
                      className={`flex flex-col items-center p-1.5 sm:p-2 rounded-lg border-2 transition-all ${
                        overallFeedbackRatings[q.key] === option.value
                          ? 'border-green-500 bg-green-50 scale-105'
                          : 'border-transparent hover:border-neutral-200 bg-white'
                      }`}
                    >
                      <span className="text-lg sm:text-xl">{option.emoji}</span>
                      <span className="text-[9px] text-neutral-500 hidden sm:block">{option.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Comment */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-1 text-left">
              Additional comments (optional)
            </label>
            <textarea
              value={overallFeedbackComment}
              onChange={(e) => setOverallFeedbackComment(e.target.value)}
              placeholder="Share your thoughts about the session..."
              rows={2}
              className="w-full px-3 py-2 text-sm text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-100 focus:border-green-600 transition-all resize-none"
            />
          </div>

          {/* Submit Button — sticky to the bottom of the modal's scroll area so
              it's always reachable on small screens without scrolling (the tap
              can't get hijacked by an in-progress scroll on iOS Safari). */}
          <div className="sticky bottom-0 -mx-2 sm:-mx-4 px-2 sm:px-4 pt-3 pb-1 bg-white">
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmitOverallFeedback}
              fullWidth
              size="lg"
              disabled={!Object.values(overallFeedbackRatings).every(r => r > 0)}
              className="bg-green-600 hover:bg-green-700 disabled:bg-neutral-300"
            >
              {Object.values(overallFeedbackRatings).every(r => r > 0)
                ? 'Submit Feedback'
                : `Answer ${5 - Object.values(overallFeedbackRatings).filter(r => r > 0).length} more questions`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Feedback Submitted Thank You Modal */}
      <Modal
        isOpen={showOverallFeedbackModal && overallFeedbackSubmitted}
        onClose={() => setShowOverallFeedbackModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-5">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Thank You!</h3>
          <p className="text-base text-neutral-600 mb-6">
            Your feedback has been submitted successfully. We appreciate your input!
          </p>
          <Button
            variant="primary"
            onClick={() => setShowOverallFeedbackModal(false)}
            fullWidth
            size="lg"
            className="bg-green-600 hover:bg-green-700"
          >
            Continue
          </Button>
        </div>
      </Modal>

      {/* Break Time Modal */}
      <Modal
        isOpen={showBreakModal}
        onClose={() => setShowBreakModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-blue-100 mb-5">
            <svg className="h-10 w-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Time for a Break!</h3>
          <p className="text-base text-neutral-600 mb-2">"{breakInfo.name}" has ended</p>
          <div className="bg-blue-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-blue-600 font-medium mb-2">Break Duration</p>
            <p className="text-4xl font-bold text-blue-700">
              {breakInfo.breakTime} <span className="text-xl font-normal">min</span>
            </p>
          </div>
          <p className="text-sm text-neutral-500 mb-6">
            Take a moment to stretch, grab a drink, or relax. The session will resume shortly.
          </p>
          <Button variant="primary" onClick={() => setShowBreakModal(false)} fullWidth size="lg">
            Got it!
          </Button>
        </div>
      </Modal>

      {/* Subsession Started Modal (Break Ended) */}
      <Modal
        isOpen={showSubsessionStartedModal}
        onClose={() => setShowSubsessionStartedModal(false)}
        title=""
        size="md"
      >
        <div className="text-center py-6 px-4">
          <div className="mx-auto inline-flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-5">
            <svg className="h-10 w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold text-neutral-900 mb-3">Session Resuming!</h3>
          <p className="text-base text-neutral-600 mb-2">
            {subsessionStartedInfo.number > 0 ? `Section ${subsessionStartedInfo.number}` : 'Next section'} is starting
          </p>
          <div className="bg-green-50 rounded-xl p-6 mb-6">
            <p className="text-sm text-green-600 font-medium mb-2">Now Starting</p>
            <p className="text-xl font-bold text-green-700">"{subsessionStartedInfo.name}"</p>
          </div>
          <p className="text-sm text-neutral-500 mb-6">
            Get ready! New activities may be launched soon.
          </p>
          <Button variant="primary" onClick={() => setShowSubsessionStartedModal(false)} fullWidth size="lg">
            I'm Ready!
          </Button>
        </div>
      </Modal>
    </>
  );

  // If showing a specific activity, render it without sidebar
  if (!showActivitiesList && currentActivity) {
    return (
      <div>
        {launchedWorkbook && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 border-b border-blue-700 shadow-lg sticky top-0 z-40">
            <div className="max-w-5xl mx-auto px-4 py-2.5 sm:px-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className="flex-shrink-0 w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-white sm:text-sm">Workbook Available</p>
                    <p className="text-xs text-blue-100 truncate">{launchedWorkbook.name}</p>
                  </div>
                </div>
                <a
                  href={launchedWorkbook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 px-3 py-1.5 sm:px-4 sm:py-2 bg-white text-blue-600 rounded-lg text-xs sm:text-sm font-semibold hover:bg-blue-50 transition-colors shadow-md"
                >
                  View
                </a>
              </div>
            </div>
          </div>
        )}
        {renderActivity()}
        {renderModals()}
      </div>
    );
  }

  // Dashboard layout with sidebar for activities list
  return (
    <div className="min-h-screen bg-neutral-100 flex">
      <ReconnectBanner isConnected={socket?.isConnected} reconnecting={socket?.reconnecting} />
      {/* ConnectionStatus shows a "Live" socket indicator — only meaningful while the session is live/paused.
          Hide when the session has ended so it doesn't read as "session is live" after the fact. */}
      {!isSessionCompleted && (
        <ConnectionStatus
          isConnected={socket?.isConnected}
          reconnecting={socket?.reconnecting}
          onReconnect={() => socket?.socket?.connect?.()}
        />
      )}
      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? 80 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:flex flex-col bg-white border-r border-neutral-200 h-screen sticky top-0"
      >
        {/* Logo */}
        <div className="p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2">
            <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            <AnimatePresence>
              {!sidebarCollapsed && (
                <motion.img
                  src={logoFull}
                  alt="Aktivity"
                  className="h-8 object-contain"
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                />
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Search */}
        {!sidebarCollapsed && (
          <div className="p-4">
            <div className="relative">
              <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search"
                className="w-full pl-10 pr-4 py-2 bg-neutral-100 border-0 rounded-lg text-sm text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>
        )}

        {/* Back Button — returns participant to the lobby's welcome view (pre/session/post phase cards) */}
        <div className="px-3 pt-2">
          <button
            onClick={() => navigate(`/session/${sessionId}/lobby?view=welcome`)}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-all text-sm"
            title={sidebarCollapsed ? 'Back to lobby' : ''}
          >
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {!sidebarCollapsed && <span>Back</span>}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3">
          <ul className="space-y-1">
            {navigationItems.map((item) => {
              // Highlight the nav item matching the current view
              // ('welcome' when activeView==='welcome', 'activities' otherwise — Content/Reports/Workspace are other routes).
              const isActive = (item.id === 'welcome' && activeView === 'welcome')
                || (item.id === 'activities' && activeView === 'activities');
              return (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className={`relative w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-white text-neutral-900 font-semibold ring-1 ring-inset ring-neutral-200/70 shadow-[0_1px_0_rgba(16,24,40,0.04),0_4px_12px_-4px_rgba(16,24,40,0.08)]'
                      : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
                  }`}
                  title={sidebarCollapsed ? item.name : ''}
                >
                  {isActive && !sidebarCollapsed && (
                    <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 rounded-r-full bg-gradient-to-b from-primary-500 to-primary-700" />
                  )}
                  <span className={`flex-shrink-0 ${isActive ? 'text-primary-600' : ''}`}>{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  {!sidebarCollapsed && item.badge && (
                    <span className="ml-auto px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                      {item.badge}
                    </span>
                  )}
                </button>
              </li>
              );
            })}
          </ul>
        </nav>

        {/* Bottom Navigation */}
        <div className="border-t border-neutral-200 py-2 px-3">
          <ul className="space-y-1">
            {bottomNavItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={item.onClick}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-neutral-700 hover:bg-neutral-100 transition-all"
                  title={sidebarCollapsed ? item.name : ''}
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  <AnimatePresence>
                    {!sidebarCollapsed && (
                      <motion.span
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.2 }}
                        className="font-medium text-sm whitespace-nowrap"
                      >
                        {item.name}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Collapse Toggle */}
        <div className="p-4 border-t border-neutral-200">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center p-2 rounded-lg text-neutral-600 hover:bg-neutral-100 transition-colors"
          >
            <svg
              className="w-5 h-5 transition-transform"
              style={{ transform: sidebarCollapsed ? 'rotate(180deg)' : 'rotate(0deg)' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Header */}
        <header className="bg-white border-b border-neutral-200 px-6 py-4 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            {/* Mobile Logo */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={() => navigate(`/session/${sessionId}/lobby`)}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-neutral-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
            </div>

            {/* Title */}
            <div className="hidden lg:block">
              <h1 className="text-xl font-semibold text-neutral-900">Aktivities</h1>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-3">
              {session?.status === 'live' ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                  <span className="text-xs font-medium hidden sm:inline">Live</span>
                </div>
              ) : (session?.status === 'completed' || session?.status === 'ended') ? (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-neutral-100 text-neutral-600 rounded-full">
                  <div className="w-2 h-2 rounded-full bg-neutral-400"></div>
                  <span className="text-xs font-medium hidden sm:inline">Ended</span>
                </div>
              ) : null}
              {session?.code && (
                <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-neutral-100 rounded-full">
                  <span className="text-xs text-neutral-500">Code:</span>
                  <span className="text-xs font-mono font-semibold text-neutral-900">{session.code}</span>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Workbook Banner */}
        {launchedWorkbook && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">Workbook Available</p>
                  <p className="text-sm text-blue-100 truncate">{launchedWorkbook.name}</p>
                </div>
              </div>
              <a
                href={launchedWorkbook.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-4 py-2 bg-white text-blue-600 rounded-lg text-sm font-semibold hover:bg-blue-50 transition-colors"
              >
                View
              </a>
            </div>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto pb-6">
          {/* Socket-blocked warning: live updates not reaching this device */}
          {socketBlocked && (
            <div className="mb-4 bg-red-50 border border-red-300 rounded-lg p-4 text-sm">
              <div className="flex items-start gap-2 mb-2">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                </svg>
                <div>
                  <p className="font-semibold text-red-900">Live updates are not reaching this device</p>
                  <p className="text-xs text-red-800 mt-0.5">
                    Your phone's privacy settings (Lockdown Mode, Content Blockers) or network is blocking our real-time connection.
                    You won't see activities as they are launched.
                  </p>
                </div>
              </div>
              <ol className="list-decimal list-inside space-y-1 text-red-800 mb-2 ml-1">
                <li>Try opening this session in <b>Chrome</b> instead of Safari.</li>
                <li>iPhone: Settings → <b>Privacy &amp; Security</b> → turn OFF <b>Lockdown Mode</b>.</li>
                <li>Disable any ad-blocker / content-blocker extensions for this site.</li>
                <li>Check your Wi-Fi — some corporate networks block WebSockets.</li>
                <li>Then reload this page.</li>
              </ol>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full mt-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg text-sm"
              >
                🔄 Reload page
              </button>
            </div>
          )}

          {/* Welcome view — same session-phase welcome block the lobby renders */}
          {activeView === 'welcome' && (
            <SessionPhaseWelcome
              session={session}
              participantCount={session?.participants?.length || 0}
              workbookViewsCount={workbookViewsCount}
              respondedActivitiesCount={respondedActivities?.size || 0}
              onContent={() => navigate(`/session/${sessionId}/workbooks`)}
              onWorkbooks={() => navigate(`/session/${sessionId}/launched-workbooks`)}
              onActivities={() => {
                setActiveView('activities');
                setShowActivitiesList(true);
                setCurrentActivity(null);
              }}
              onWorkspace={() => navigate(`/session/${sessionId}/notes`)}
            />
          )}

          {/* Activities list — default view */}
          {activeView === 'activities' && (<>
          {/* Header */}
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-neutral-900">
                {session?.status === 'live' ? 'Live Aktivities' : 'Aktivities'}
              </h2>
              <p className="text-neutral-500 mt-1">
                {session?.status === 'live'
                  ? 'Respond to trainer-launched activities'
                  : (session?.status === 'completed' || session?.status === 'ended')
                    ? 'Review the activities from this session'
                    : 'Activities will appear here once the trainer launches them'}
              </p>
            </div>
            <div className="text-right flex items-center gap-2">
              {/* Refresh Button for Mobile */}
              <button
                onClick={async () => {
                  console.log('[Participant] Manual refresh triggered');
                  try {
                    const activitiesResponse = await participantService.getActiveActivities(sessionId);
                    if (activitiesResponse.success && activitiesResponse.data) {
                      setLaunchedActivities(activitiesResponse.data.map(act => ({
                        ...act,
                        launchedAt: act.launchedAt || new Date(),
                      })));
                      showToast({ title: 'Refreshed', message: `${activitiesResponse.data.length} activities`, icon: '🔄', color: 'blue' });
                    }
                  } catch (error) {
                    console.error('[Participant] Error refreshing activities:', error);
                  }
                }}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors lg:hidden"
                title="Refresh activities"
              >
                <svg className="w-5 h-5 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <div>
                <p className="text-sm text-neutral-500">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                </p>
                <p className="text-xs text-neutral-400 mt-1">
                  {launchedActivities.length} {launchedActivities.length === 1 ? 'activity' : 'activities'} launched
                </p>
              </div>
            </div>
          </div>

          {/* Overall Feedback card — visible as long as it's launched & not ended,
              so participants who missed the popup (network drop, late rejoin) can still submit. */}
          {overallFeedbackStatus.launched && !overallFeedbackStatus.endedAt && (
            <div className="mb-4">
              <div
                onClick={() => {
                  if (!overallFeedbackStatus.hasSubmitted && !overallFeedbackSubmitted) {
                    setShowOverallFeedbackModal(true);
                  }
                }}
                className={`bg-white rounded-xl border p-5 transition-all relative ${
                  overallFeedbackStatus.hasSubmitted || overallFeedbackSubmitted
                    ? 'border-neutral-300 bg-neutral-50 cursor-default'
                    : 'border-amber-300 hover:shadow-md cursor-pointer'
                }`}
              >
                {!(overallFeedbackStatus.hasSubmitted || overallFeedbackSubmitted) && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ backgroundColor: '#ff8a00' }}>
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                )}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-amber-100 rounded-xl flex items-center justify-center">
                    <svg className="w-7 h-7 text-amber-600" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-base font-semibold text-neutral-900">Overall Session Feedback</h3>
                      <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Live</span>
                    </div>
                    <p className="text-sm text-neutral-500 line-clamp-1">
                      Rate this session on 5 dimensions and share your thoughts.
                    </p>
                  </div>
                  <div>
                    {(overallFeedbackStatus.hasSubmitted || overallFeedbackSubmitted) ? (
                      <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                        Submitted
                      </div>
                    ) : (
                      <Button variant="primary" size="sm">Give Feedback</Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Show all launched activities (including ended ones) */}
          {launchedActivities.length === 0 ? (
            // Empty State - No activities launched yet
            <div className="bg-white rounded-xl border border-neutral-200 p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-50 rounded-full mb-4">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Aktivities Yet</h3>
              <p className="text-sm text-neutral-500 max-w-md mx-auto mb-4">
                Waiting for the trainer to launch aktivities. Stay on this page and keep your connection active.
              </p>
              <div className="inline-flex items-center gap-2 text-sm text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Connected</span>
              </div>
            </div>
          ) : (
            // Activities Grid - sorted by launchedAt descending (newest first)
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...launchedActivities].sort((a, b) => {
                const timeA = new Date(a.launchedAt || 0).getTime();
                const timeB = new Date(b.launchedAt || 0).getTime();
                return timeB - timeA; // Descending order (newest first)
              }).map((activity) => {
                const actId = getActivityId(activity);
                // More robust check for whether the participant has responded
                const isResponded = respondedActivities.has(actId) ||
                                   activity.hasResponded ||
                                   activity.userResponse ||
                                   activity.responded ||
                                   (activity.responses && activity.responses.some(r =>
                                     r.participantId === participantId || r.participantName === participantName
                                   ));
                const isCompleted = activity.status === 'completed';
                const isActive = activity.status === 'active' || !activity.status;

                return (
                  <div
                    key={actId}
                    onClick={() => handleStartActivity(activity)}
                    className={`bg-white rounded-xl border p-5 transition-all cursor-pointer relative ${
                      isCompleted
                        ? 'border-neutral-300 bg-neutral-50'
                        : 'border-neutral-200 hover:shadow-md'
                    }`}
                  >
                    {/* Status Badge */}
                    {isActive && !isResponded && (
                      <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg animate-bounce" style={{ backgroundColor: '#ff8a00' }}>
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                        </svg>
                      </div>
                    )}
                    {isCompleted && (
                      <div className="absolute -top-2 -right-2 px-2 py-1 bg-neutral-500 text-white text-xs font-medium rounded-full shadow-lg">
                        Ended
                      </div>
                    )}

                    <div className="flex flex-col items-center text-center">
                      <div className={`w-14 h-14 ${getActivityTypeColor(activity.type)} rounded-xl flex items-center justify-center mb-4 ${isResponded || isCompleted ? 'opacity-60' : ''}`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <h3 className={`text-base font-semibold mb-1 ${isCompleted ? 'text-neutral-600' : 'text-neutral-900'}`}>{activity.title}</h3>
                      <p className="text-sm text-neutral-500 mb-3 line-clamp-2">{activity.question || activity.description}</p>
                      {/* Duration badge — only rendered when the trainer enabled a timer for
                          this activity. An untimed activity shouldn't advertise a minute count. */}
                      {activity.timerEnabled === true && resolveTimeLimitSec(activity) > 0 && (
                        <div className="flex items-center gap-2 text-xs text-neutral-400 mb-4">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>{Math.floor(resolveTimeLimitSec(activity) / 60)} min</span>
                        </div>
                      )}

                      {isResponded ? (
                        <div className="w-full px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          Responded
                        </div>
                      ) : isCompleted ? (
                        <div className="w-full px-4 py-2 bg-amber-100 text-amber-700 rounded-lg font-semibold text-sm flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Not Attended
                        </div>
                      ) : (
                        <Button variant="primary" size="sm" className="w-full">
                          Answer Now
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          </>)}
          {/* Mobile spacer — ensures the last activity card's status badge
              ("Responded" / "Not Attended") isn't overlapped by the fixed
              bottom nav bar. Uses inline style so it works regardless of
              Tailwind JIT cache state. Hidden on desktop. */}
          <div
            className="lg:hidden"
            aria-hidden="true"
            style={{ height: 'calc(72px + env(safe-area-inset-bottom, 0px))' }}
          />
        </main>
      </div>

      {/* Mobile Bottom Navigation - Show all items with smaller sizes */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 z-40 safe-area-inset-bottom">
        <nav className="flex justify-around items-center h-14 px-0.5">
          {/* Active-state logic mirrors the desktop sidebar: Welcome/Aktivity tabs
              drive off `activeView`; other items (Content/Notes/Reports) are route
              links, so they're not treated as "active" here. */}
          {navigationItems.map((item) => {
            const isActive = (item.id === 'welcome' && activeView === 'welcome')
              || (item.id === 'activities' && activeView === 'activities');
            return (
              <button
                key={item.id}
                onClick={item.onClick}
                className={`flex flex-col items-center justify-center gap-0 px-0.5 py-1 flex-1 min-w-0 transition-colors ${
                  isActive ? '' : 'text-neutral-500'
                }`}
                style={isActive ? { color: '#ff8a00' } : {}}
              >
                <span className="relative [&>svg]:w-4 [&>svg]:h-4">
                  {item.icon}
                  {item.badge && (
                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  )}
                </span>
                <span className="text-[8px] font-medium truncate w-full text-center leading-tight mt-0.5">
                  {item.id === 'activities' ? 'Aktivity' : item.id === 'workspace' ? 'Notes' : item.name}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Shared Modals */}
      {renderModals()}
    </div>
  );
};

export default ParticipantActiveSessionPage;
